/**
 * Fase 7 Test Component
 * 
 * Test component for Statistics & Analytics components:
 * - StatisticsPanel
 * - ActivityFeed
 * - TopLocations
 * 
 * Provides controls to:
 * - Generate mock events
 * - Simulate real-time activity
 * - Test filtering and search
 * - Test navigation callbacks
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 7 Testing
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, Play, Pause, RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { Card } from '../../../common/Card';
import { StatisticsPanel } from './StatisticsPanel';
import { ActivityFeed } from './ActivityFeed';
import { TopLocations } from './TopLocations';
import { ActivityCacheManager, TimeWindow } from './ActivityCacheManager';
import { ActivityQueueManager } from './ActivityQueueManager';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';

/**
 * Generate mock activity event
 */
const generateMockEvent = (
  binCode?: string,
  rackNumber?: number,
  activityType?: ActivityType
): LocationActivityEvent => {
  const racks = [5, 8, 12, 15, 20];
  const operators = ['OP001', 'OP002', 'OP003', 'OP004'];
  const items = ['ITEM-001', 'ITEM-002', 'ITEM-003', 'ITEM-004', 'ITEM-005'];
  const activityTypes = [
    ActivityType.Pick,
    ActivityType.PutAway,
    ActivityType.Transfer,
    ActivityType.CorrectionIn,
    ActivityType.CorrectionOut,
    ActivityType.Counting,
    ActivityType.Receiving,
    ActivityType.Reservation
  ];

  const selectedRack = rackNumber || racks[Math.floor(Math.random() * racks.length)];
  const selectedActivityType = activityType || activityTypes[Math.floor(Math.random() * activityTypes.length)];
  
  // Generate BinCode if not provided
  const generatedBinCode = binCode || 
    `WH01-R${selectedRack.toString().padStart(2, '0')}-V${Math.floor(Math.random() * 5 + 1).toString().padStart(2, '0')}-S${Math.floor(Math.random() * 10 + 1).toString().padStart(2, '0')}`;

  return {
    binCode: generatedBinCode,
    rackNumber: selectedRack,
    activityType: selectedActivityType,
    timestamp: new Date().toISOString(),
    operatorId: operators[Math.floor(Math.random() * operators.length)],
    itemNo: items[Math.floor(Math.random() * items.length)],
    quantity: Math.floor(Math.random() * 20) + 1,
    companyNo: 'COMP001',
    warehouseId: 'WH01'
  };
};

export const Fase7Test: React.FC = () => {
  const [cacheManager] = useState(() => new ActivityCacheManager({
    maxCacheSize: 1000,
    enablePersistence: false, // Disable for testing
    enableMidnightReset: false
  }));
  
  const [queueManager] = useState(() => new ActivityQueueManager());
  
  const [events, setEvents] = useState<LocationActivityEvent[]>([]);
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(2000); // 2 seconds
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<TimeWindow>(TimeWindow.Today);

  /**
   * Add mock event to cache and update state
   */
  const addMockEvent = (event?: LocationActivityEvent) => {
    const newEvent = event || generateMockEvent();
    cacheManager.addEvent(newEvent);
    queueManager.enqueue(newEvent);
    setEvents(cacheManager.getAllEvents());
  };

  /**
   * Simulation effect
   */
  useEffect(() => {
    if (!isSimulationRunning) return;

    const interval = setInterval(() => {
      addMockEvent();
    }, simulationSpeed);

    return () => clearInterval(interval);
  }, [isSimulationRunning, simulationSpeed]);

  /**
   * Generate burst of events for specific scenarios
   */
  const generateScenario = (scenario: string) => {
    switch (scenario) {
      case 'busy-rack':
        // Generate 20 events for rack 5
        for (let i = 0; i < 20; i++) {
          addMockEvent(generateMockEvent(undefined, 5));
        }
        break;
      case 'corrections':
        // Generate correction events
        for (let i = 0; i < 10; i++) {
          addMockEvent(generateMockEvent(undefined, undefined, 
            Math.random() > 0.5 ? ActivityType.CorrectionIn : ActivityType.CorrectionOut));
        }
        break;
      case 'picking-spree':
        // Generate picking events
        for (let i = 0; i < 15; i++) {
          addMockEvent(generateMockEvent(undefined, undefined, ActivityType.Pick));
        }
        break;
      case 'multi-rack':
        // Generate events across multiple racks
        [5, 8, 12, 15, 20].forEach(rack => {
          for (let i = 0; i < 5; i++) {
            addMockEvent(generateMockEvent(undefined, rack));
          }
        });
        break;
    }
  };

  /**
   * Clear all events
   */
  const clearAllEvents = () => {
    cacheManager.clear(false);
    queueManager.clear();
    setEvents([]);
  };

  /**
   * Handle event click from ActivityFeed
   */
  const handleEventClick = (event: LocationActivityEvent) => {
    console.log('Event clicked:', event);
    alert(`Navigating to ${event.binCode}\nReol: ${event.rackNumber}\nVare: ${event.itemNo}`);
  };

  /**
   * Handle location click from TopLocations
   */
  const handleLocationClick = (binCode: string) => {
    console.log('Location clicked:', binCode);
    alert(`Navigating to location: ${binCode}`);
  };

  /**
   * Handle rack click from TopLocations
   */
  const handleRackClick = (rackNumber: number) => {
    console.log('Rack clicked:', rackNumber);
    alert(`Navigating to rack: ${rackNumber}`);
  };

  const queueStats = queueManager.getStats();
  const cacheStats = cacheManager.getStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
        <div className="flex items-center gap-3">
          <BarChart3 size={32} />
          <div>
            <h2 className="text-2xl font-bold">FASE 7: Statistics & Analytics Test</h2>
            <p className="text-blue-100">Test statistikkpanel, aktivitetsfeed og top lokasjoner</p>
          </div>
        </div>
      </Card>

      {/* Controls */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Test Kontroller</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Simulation Controls */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700">Simulering</h4>
            
            <div className="flex gap-2">
              <button
                onClick={() => setIsSimulationRunning(!isSimulationRunning)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
                  isSimulationRunning
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isSimulationRunning ? <Pause size={16} /> : <Play size={16} />}
                {isSimulationRunning ? 'Stopp' : 'Start'} Simulering
              </button>
              
              <button
                onClick={() => addMockEvent()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
              >
                <RefreshCw size={16} />
                Legg til hendelse
              </button>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Simulering hastighet: {simulationSpeed}ms
              </label>
              <input
                type="range"
                min="500"
                max="5000"
                step="500"
                value={simulationSpeed}
                onChange={(e) => setSimulationSpeed(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          {/* Scenario Buttons */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700">Testscenarioer</h4>
            
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => generateScenario('busy-rack')}
                className="px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium"
              >
                Travelt på Reol 5
              </button>
              
              <button
                onClick={() => generateScenario('corrections')}
                className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium"
              >
                Korreksjoner
              </button>
              
              <button
                onClick={() => generateScenario('picking-spree')}
                className="px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium"
              >
                Plukkeaktivitet
              </button>
              
              <button
                onClick={() => generateScenario('multi-rack')}
                className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium"
              >
                Flere reoler
              </button>
            </div>

            <button
              onClick={clearAllEvents}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
            >
              <Trash2 size={16} />
              Fjern alle hendelser
            </button>
          </div>
        </div>

        {/* Time Window Selector */}
        <div className="pt-4 border-t border-gray-200">
          <label className="block text-sm text-gray-700 mb-2">Tidsvindu for statistikk:</label>
          <div className="flex gap-2">
            {[
              { value: TimeWindow.Last1Hour, label: 'Siste time' },
              { value: TimeWindow.Last4Hours, label: 'Siste 4 timer' },
              { value: TimeWindow.Last8Hours, label: 'Siste 8 timer' },
              { value: TimeWindow.Today, label: 'I dag' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSelectedTimeWindow(value)}
                className={`px-4 py-2 rounded-lg font-medium ${
                  selectedTimeWindow === value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Statistics Display */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{events.length}</div>
              <div className="text-sm text-gray-600">Totale hendelser</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{queueStats.queueLength}</div>
              <div className="text-sm text-gray-600">Reoler i kø</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{cacheStats.usagePercentage.toFixed(1)}%</div>
              <div className="text-sm text-gray-600">Cache benyttet</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Statistics Panel */}
        <div className="lg:col-span-1">
          <StatisticsPanel
            allEvents={events}
            queueStats={queueStats}
            currentRack={queueStats.queueLength > 0 ? queueManager.peek()?.rackNumber || null : null}
            currentWarehouseId="WH01"
            updateInterval={1000}
          />
        </div>

        {/* Middle column: Activity Feed */}
        <div className="lg:col-span-1">
          <ActivityFeed
            events={events}
            maxEvents={50}
            autoScroll={true}
            onEventClick={handleEventClick}
          />
        </div>

        {/* Right column: Top Locations */}
        <div className="lg:col-span-1">
          <TopLocations
            events={events}
            timeWindow={selectedTimeWindow}
            topLocationsCount={10}
            topRacksCount={5}
            onLocationClick={handleLocationClick}
            onRackClick={handleRackClick}
          />
        </div>
      </div>

      {/* Info Box */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">📊 Testing Guide - FASE 7</p>
            <div className="space-y-1">
              <p><strong>StatisticsPanel:</strong> Viser sanntidsstatistikk med trendpiler. Oppdateres hvert sekund.</p>
              <p><strong>ActivityFeed:</strong> Scrollbar liste med siste 50 hendelser. Test filtrering, søk og auto-scroll pause.</p>
              <p><strong>TopLocations:</strong> Topp 10 lokasjoner og topp 5 reoler basert på intensitetsscore.</p>
              <p className="mt-2"><strong>Test scenarios:</strong></p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Travelt på Reol 5:</strong> 20 hendelser på én reol for å teste topp-ranking</li>
                <li><strong>Korreksjoner:</strong> 10 korreksjons-hendelser (høy vekting)</li>
                <li><strong>Plukkeaktivitet:</strong> 15 plukke-hendelser</li>
                <li><strong>Flere reoler:</strong> 25 hendelser fordelt på 5 reoler</li>
              </ul>
              <p className="mt-2"><strong>Tips:</strong> Klikk på hendelser i feed eller lokasjoner i top-lists for å teste navigasjon.</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Fase7Test;
